import echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/line';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/candlestick';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/axisPointer';
import 'echarts/lib/component/dataZoom';
import 'echarts/lib/component/markPoint';
import 'echarts/lib/component/markLine';
import KLineEChartsFormatter from './KLineEChartsFormatter';

/**
 * KLineSetChartController类定义
 */
class KLineSetChartController {
    /**
     * KLineSetChartController类构造函数
     * @param {Object} chartOption 加载的图表选项
     */
    constructor(chartOption = {}) {
        this.chartObj = null;
        this.chartOption = chartOption;
        this.chartFormatter = new KLineEChartsFormatter();
    }

    /**
     * 初始化ECharts
     * @param {HTMLElement} DOM 界面DOM元素
     */
    initECharts(DOM) {
        this.chartObj = echarts.init(DOM);

        if (this.chartOption.dataZoom instanceof Array) {
            this.chartObj.zoomStart = this.chartOption.dataZoom[0].start;
            this.chartObj.zoomEnd = this.chartOption.dataZoom[0].end;
        }

        if (this.chartOption.tooltip) {
            this.chartOption.tooltip.formatter = this.chartFormatter.getTooltipFormatter(
                this.chartObj.getHeight(),
                this.chartOption.grid
            );
        }

        // 先应用不带数据的图表选项
        this.chartObj.setOption(this.chartOption);

        // 动态调整图表的宽高
        window.onresize = () => {
            this.chartObj.resize();
        };
        // 监听点击事件
        this.chartObj.on(
            'click',
            function(event) {
                switch (event.componentType) {
                    case 'markPoint':
                        this.markPointOnClick(event);
                        break;
                    case 'series':
                    default:
                        break;
                }
            }.bind(this)
        );
        // 监听滑块位置，以便重新获取数据后调整滑块位置
        this.chartObj.on('dataZoom', param => {
            this.chartObj.zoomStart = typeof param.start === 'number' ? param.start : param.batch[0].start;
            this.chartObj.zoomEnd = typeof param.end === 'number' ? param.end : param.batch[0].end;
        });
    }

    /**
     * MarkPoint点击事件
     * @param {Object} event ECharts事件对象
     */
    markPointOnClick(event) {

    }

    /**
     * 隐藏markPoint的label
     * @param {Object} event ECharts事件对象
     */
    hideMarkPointLabel(event) {
        let markPoint = Object.assign({}, this.chartObj.getOption().series[0].markPoint);
        markPoint.data.forEach((data, index) => {
            // 固定的点（比如：最大值、最小值等）没有label
            if (data.type) {
                return;
            }
            // 因为event.dataIndex不一定是正确的索引，所以只好比较coord来判断是否是同一个点
            let eventCoord = [];
            if (event.data && event.data.coord instanceof Array) {
                eventCoord = event.data.coord;
            }
            let isSame = data.coord.every((item, i) => {
                return item === eventCoord[i];
            });
            // 如果是当前点，则切换label显示状态，否则隐藏
            if (isSame) {
                data.label.show = !data.label.show;
            } else {
                data.label.show = false;
            }
        });
        let option = {
            series: [
                {
                    markPoint
                }
            ]
        };
        this.chartObj.setOption(option);
    }

    /**
     * 重置滑块位置
     * @param {Array<String>} times 时间序列
     */
    resetDataZoom(times) {
        // 根据数据量调整滑块大小。按每页显示条数，计算起始百分比
        if (times.length === 0) {
            return;
        }

        // 每屏显示90个点
        let zoomRange = Math.min(90 / times.length, 1) * 100;
        let zoomStart = this.chartObj.zoomStart;
        let zoomEnd = this.chartObj.zoomEnd;
        let realZoomRange = zoomEnd - zoomStart;
        let rangeDifference = zoomRange - realZoomRange;

        if (zoomStart === 0) {
            zoomEnd = zoomStart + zoomRange;
        } else if (zoomEnd === 100) {
            zoomStart = zoomEnd - zoomRange;
        } else {
            zoomStart = Math.max(zoomStart - rangeDifference / 2, 0);
            zoomEnd = Math.min(zoomEnd + rangeDifference / 2, 100);
        }
        this.chartObj.dispatchAction({
            type: 'dataZoom',
            start: zoomStart,
            end: zoomEnd
        });
    }

    /**
     * 获取初始的ECharts选项
     * @param {Array<String>} xAxisDatas X轴数据集合
     * @param {String} type 时间类型
     * @returns {Object} 返回ECharts选项
     */
    getInitOption(xAxisDatas, type) {
        let option = {
            tooltip: {
                alwaysShowContent: true
            },
            axisPointer: {
                label: {
                    formatter: this.chartFormatter.axisPointerFormatter
                }
            },
            xAxis: [],
            yAxis: [],
            series: []
        };

        option.xAxis = this.chartOption.xAxis.map(() => {
            return {
                data: xAxisDatas
            };
        });

        // 增加坐标轴格式化方法
        if (option.xAxis.length > 2) {
            // 只有最后一个x轴需要格式化
            option.xAxis[option.xAxis.length - 1].axisLabel = {
                formatter: this.chartFormatter.getAxisFormatter(type)
            };
        }

        option.yAxis = this.chartOption.yAxis.map((axis, index) => {
            let formatter;
            switch (index) {
                case 1: // 成交量Y轴格式化函数
                case 2:
                    formatter = this.chartFormatter.volumeYAxisFormatter;
                    break;
                default:
                    formatter = this.chartFormatter.getAxisFormatter(type, 'yAxis');
                    break;
            }
            return {
                axisLabel: {
                    formatter
                }
            };
        });

        return option;
    }

    /**
     * 接受图表选项
     * @param {Object} option 要应用的图表选项
     * @param {Array<String>} y0Datas 第0个y轴绑定的数据集合
     * @param {Array<String>} y2Datas 第2个y轴绑定的数据集合
     */
    applyOptionToChart(option, y0Datas, y2Datas) {
        // 根据数据动态调整字体
        let data = y0Datas;
        if (data.length > 0 && Math.max(...data) < 11) {
            option.axisPointer.label.fontSize = 8;
            option.yAxis[0].axisLabel.fontSize = 8;
            if (option.yAxis.length > 1) {
                option.yAxis[1].axisLabel.fontSize = 8;
            }
        }
        data = y2Datas;
        if (option.yAxis.length > 2 && data.length > 0 && Math.max(...data) < 11) {
            option.yAxis[2].axisLabel.fontSize = 8;
        }

        // dispatchAction的hideTip无效，所以只好修改tooltip选项
        if (option.xAxis[0].length === 0) {
            option.tooltip.alwaysShowContent = false;
        }

        this.chartFormatter.setChartOption(option);
        this.chartObj.setOption(option);

        // 触发显示tooltip。因为切换指标后要重新刷新tooltip
        this.chartObj.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: option.series[0].data.length - 1
        });
    }
}

export default KLineSetChartController;



// WEBPACK FOOTER //
// ./src/controller/KLineSetChart.js